/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect } from 'react'
import { useLazyQuery, gql, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import moment from 'moment'
import ChartLine from '../../../../components/charts/ChartLine'
import { ChartLineThemes } from '../../../../components/charts/types/ChartLine'
import ChartLineSkeleton from '../../../../components/skeletons/ChartLineSkeleton'
import { formatNumber, formatDateCard } from '../../../../utils/Helpers'
import { CartChangeFiltersContext } from '../../../../context/CartChangeFiltersContext'

const MESSAGE_CHANGE_OPENED = 'Exibiu modal'
const MESSAGE_CHANGE_ACCEPTED = 'Trocou de cesta'
const MESSAGE_CHANGE_DISMISSED = 'Manteve a cesta atual'

const CHANGE_OPENED = 'opened'
const CHANGE_ACCEPTED = 'accepted'
const CHANGE_DISMISSED = 'dismissed'

const SET_OPENED = 'CART_CHANGE_OPENED'
const SET_ACCEPTED = 'CART_CHANGE_ACCEPTED'
const SET_DISMISSED = 'CART_CHANGE_DISMISSED'

const GET_USER_COMPARISON_DECISION = gql`
  query GetUserComparisionDecision($filters: EventCartChangeFilter) {
    eventCartChangeAggs(
      size: 0,
      filters: $filters,
      index: "eventos-fapp-cart-change-*",
      aggs: {
        dia: {
          date_histogram: {
            field: "date",
            calendar_interval: "day",
            time_zone: "-03:00"
          },
          aggs: {
            ${CHANGE_OPENED}: {
              filter: {
                term: {
                  event_keyword: "${SET_OPENED}"
                }
              }
            },
            ${CHANGE_ACCEPTED}: {
              filter: {
                term: {
                  event_keyword: "${SET_ACCEPTED}"
                }
              }
            },
            ${CHANGE_DISMISSED}: {
              filter: {
                term: {
                  event_keyword: "${SET_DISMISSED}"
                }
              }
            }
          }
        }
      }
    )
    {
      aggregations
    }
  }
`

type BucketTypes = {
  key_as_string: string,
  key: number,
  doc_count: number,
  opened: {
    doc_count : number
  },
  accepted: {
    doc_count : number
  },
  dismissed: {
    doc_count : number
  }
}

type Series = {
  name: string
  data: number[]
}

const UserComparisonDecision = (): JSX.Element => {
  const { filters } = useContext(CartChangeFiltersContext)
  const categories: Array<string> = []
  const series: Array<Series> = []

  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_USER_COMPARISON_DECISION, {
      notifyOnNetworkStatusChange: true
    }
  )

  series.push({ name: SET_OPENED, data: [] })
  series.push({ name: SET_ACCEPTED, data: [] })
  series.push({ name: SET_DISMISSED, data: [] })

  useEffect(() => {
    getData({
      variables: { filters }
    })
  }, [filters])

  if (networkStatus === NetworkStatus.refetch) return <ChartLineSkeleton />
  if (loading || !data) return <ChartLineSkeleton />
  if (error) return <p>Error :(</p>
  if (!data.eventCartChangeAggs.aggregations.dia) return <p>No data found</p>

  const { buckets } = data.eventCartChangeAggs.aggregations.dia

  series.map(serie => {
    buckets.map((bucket: BucketTypes) => {
      if (serie.name === SET_OPENED) {
        serie.data.push(bucket.opened.doc_count)
      } else if (serie.name === SET_ACCEPTED) {
        serie.data.push(bucket.accepted.doc_count)
      } else if (serie.name === SET_DISMISSED) {
        serie.data.push(bucket.dismissed.doc_count)
      } else {
        serie.data.push(0)
      }
    })
  })

  buckets.map((bucket: BucketTypes) => {
    categories.push(bucket.key_as_string)
  })

  const options = {
    chart: {
      id: 'comparison-user-decision-cart'
    },
    xaxis: {
      categories,
      type: 'datetime',
      labels: {
        formatter: (value: Date) => {
          return moment(value).format('DD-MM-YYYY')
        }
      }
    },
    yaxis: {
      categories,
      labels: {
        formatter: (value: number) => {
          return formatNumber(value)
        }
      }
    }
  }

  series.map(alter => {
    if (alter.name === SET_OPENED) {
      alter.name = MESSAGE_CHANGE_OPENED
    } else if (alter.name === SET_ACCEPTED) {
      alter.name = MESSAGE_CHANGE_ACCEPTED
    } else {
      alter.name = MESSAGE_CHANGE_DISMISSED
    }
  })

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>Comparativo de decisões do usuário sobre a cesta ({formatDateCard(filters.date?.initialDate, filters.date?.finalDate)})</Typography>
    <ChartLine options={options} series={series} theme={ChartLineThemes.rainbow} height={320} type="area" />
  </Paper>
}

export default UserComparisonDecision
